// SwapRequests — manager view of /v1/schedules/swap-requests.
// Status filter pills (pending / approved / rejected / cancelled / 全部),
// list with both sides of the swap rendered side-by-side, approve / reject
// drawer with optional decision_notes.

import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, RefreshCw, ServerCrash, ThumbsDown, ThumbsUp, Inbox } from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import EmptyState from "../components/EmptyState.js";
import Drawer from "../components/Drawer.js";
import { Field } from "../components/Field.js";
import {
  listSwapRequests, decideSwapRequest, friendlySwapError,
  type SwapRequestRow, type SwapStatus,
} from "../lib/swapRequestsApi.js";
import { listScheduleOfficers, type OfficerPickerRow } from "../lib/rosterApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";

const STATUS_FILTERS: { value: SwapStatus | ""; label: string }[] = [
  { value: "pending", label: "待審核" },
  { value: "approved", label: "已通過" },
  { value: "rejected", label: "已拒絕" },
  { value: "cancelled", label: "已取消" },
  { value: "", label: "全部" },
];

const STATUS_BADGE: Record<SwapStatus, string> = {
  pending: "badge-warning",
  approved: "badge-success",
  rejected: "badge-danger",
  cancelled: "badge-default",
  completed: "badge-success",
};
const STATUS_LABEL: Record<SwapStatus, string> = {
  pending: "待審核", approved: "已通過", rejected: "已拒絕", cancelled: "已取消", completed: "已完成",
};

export default function SwapRequests(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const canDecide = session.scopes.includes("write:schedule") || session.super_admin === true;

  const [statusFilter, setStatusFilter] = useState<SwapStatus | "">("pending");
  const [rows, setRows] = useState<SwapRequestRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<SwapRequestRow | null>(null);
  const [officers, setOfficers] = useState<OfficerPickerRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listScheduleOfficers(tenantParam);
        if (!cancelled) setOfficers(list);
      } catch { /* officer picker is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [tenantParam]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await listSwapRequests({ tenantId: tenantParam, status: statusFilter });
      setRows(r.items);
    } catch (e) { setErr(friendlySwapError(e)); }
    finally { setLoading(false); }
  }, [tenantParam, statusFilter]);
  useEffect(() => { void load(); }, [load]);

  return (
    <>
      <PageHeader
        title="換班單"
        description="員工提出的換班 / 找替班請求；按通過後 roster 會自動換 officer。"
        actions={
          <button className="btn-secondary text-sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> 重新整理
          </button>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <button key={f.value} onClick={() => setStatusFilter(f.value)}
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              statusFilter === f.value
                ? "bg-accent-subtle border-accent/40 text-accent"
                : "bg-bg-surface border-border text-text-secondary hover:bg-bg-hover"
            }`}>{f.label}</button>
        ))}
      </div>

      {err && (
        <EmptyState icon={ServerCrash} title="換班單載入失敗" description={err}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />
      )}

      {!err && rows.length === 0 && !loading && (
        <EmptyState icon={Inbox} title="目前沒有換班單" description={statusFilter === "pending" ? "員工提出換班時會出現在這裡。" : "切回「待審核」可看待處理的換班。"} />
      )}

      {!err && rows.length > 0 && (
        <div className="card divide-y divide-border">
          {rows.map((r) => (
            <button key={r.id} onClick={() => setEditing(r)}
              className="w-full text-left px-4 py-3 hover:bg-bg-hover transition-colors">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={STATUS_BADGE[r.status]}>{STATUS_LABEL[r.status]}</span>
                    <span className="text-xs text-text-muted font-mono">{r.requested_at.slice(0, 16).replace("T", " ")}</span>
                    {r.target_roster_entry_id == null && (
                      <span className="badge-default text-xs">找替班</span>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <SidePanel
                      label="原本"
                      officer={r.requester_name ?? r.requester_officer_id}
                      date={r.requester_work_date}
                      shift={r.requester_shift_name} time={`${r.requester_shift_start ?? "??"}–${r.requester_shift_end ?? "??"}`}
                      site={r.requester_site_name}
                    />
                    <SidePanel
                      label="換成"
                      officer={r.target_name ?? r.target_officer_id}
                      date={r.target_work_date}
                      shift={r.target_shift_name} time={r.target_shift_start ? `${r.target_shift_start}–${r.target_shift_end}` : null}
                      site={r.target_site_name}
                    />
                  </div>
                  {r.reason && <p className="mt-2 text-xs text-text-secondary">原因：{r.reason}</p>}
                  {r.decision_notes && <p className="mt-1 text-xs text-text-muted">主管備註：{r.decision_notes}</p>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <Drawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title="審核換班單"
        description={editing ? `${editing.requester_name ?? editing.requester_officer_id} 的請求` : ""}
      >
        {editing && (
          <DecisionForm
            row={editing}
            tenantId={tenantParam}
            canDecide={canDecide}
            officers={officers}
            onDone={() => { setEditing(null); void load(); }}
            onClose={() => setEditing(null)}
          />
        )}
      </Drawer>
    </>
  );
}

function SidePanel({ label, officer, date, shift, time, site }: {
  label: string; officer: string; date: string | null;
  shift: string | null; time: string | null; site: string | null;
}): React.ReactElement {
  return (
    <div className="rounded border border-border bg-bg-surface p-2">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className="mt-0.5 truncate font-medium text-text-primary">{officer}</div>
      <div className="text-xs text-text-secondary truncate">
        {date ? <span className="font-mono">{date}</span> : "—"}
        {shift ? <> ・ {shift}</> : null}
        {time ? <span className="font-mono ml-1">{time}</span> : null}
      </div>
      {site ? <div className="text-xs text-text-muted truncate">{site}</div> : null}
    </div>
  );
}

function DecisionForm({ row, tenantId, canDecide, officers, onDone, onClose }: {
  row: SwapRequestRow;
  tenantId: string | null;
  canDecide: boolean;
  officers: OfficerPickerRow[];
  onDone: () => void; onClose: () => void;
}): React.ReactElement {
  const [notes, setNotes] = useState("");
  const [assignee, setAssignee] = useState<string>(row.target_officer_id ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const needsAssignee = !row.target_officer_id;

  const decide = async (decision: "approved" | "rejected") => {
    if (decision === "approved" && needsAssignee && !assignee) {
      setErr("這是「找替班」請求，通過時需要指派接班人。");
      return;
    }
    setBusy(true); setErr(null);
    try {
      await decideSwapRequest(
        row.id, decision,
        { notes: notes.trim() || null, assigneeOfficerId: decision === "approved" ? (assignee || null) : null },
        tenantId,
      );
      onDone();
    } catch (e) { setErr(friendlySwapError(e)); }
    finally { setBusy(false); }
  };

  if (row.status !== "pending") {
    return (
      <div className="space-y-3 text-sm">
        <div className="rounded border border-border bg-bg-surface p-3">
          這筆換班已決議：<span className={STATUS_BADGE[row.status]}>{STATUS_LABEL[row.status]}</span>
          {row.decided_at ? <span className="ml-2 font-mono text-xs text-text-muted">{row.decided_at.slice(0, 16).replace("T", " ")}</span> : null}
        </div>
        {row.decision_notes && <div className="text-xs text-text-secondary">主管備註：{row.decision_notes}</div>}
        <div className="flex justify-end"><button onClick={onClose} className="btn-secondary text-sm">關閉</button></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <SidePanel
          label="原本" officer={row.requester_name ?? row.requester_officer_id}
          date={row.requester_work_date} shift={row.requester_shift_name}
          time={`${row.requester_shift_start ?? "??"}–${row.requester_shift_end ?? "??"}`}
          site={row.requester_site_name}
        />
        <SidePanel
          label="換成" officer={row.target_name ?? row.target_officer_id}
          date={row.target_work_date} shift={row.target_shift_name}
          time={row.target_shift_start ? `${row.target_shift_start}–${row.target_shift_end}` : null}
          site={row.target_site_name}
        />
      </div>
      {row.reason && <div className="text-sm text-text-secondary">原因：{row.reason}</div>}
      {needsAssignee && (
        <Field label="指派接班人" required hint="這是「找替班」請求，需要指派一位員工。">
          <select className="select" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">— 請選擇 —</option>
            {officers
              .filter((o) => o.id !== row.requester_officer_id)
              .map((o) => <option key={o.id} value={o.id}>{o.display_name}{o.status !== "active" ? `（${o.status}）` : ""}</option>)}
          </select>
        </Field>
      )}
      <Field label="決議備註" hint="（選填）通過 / 拒絕後存進操作紀錄，雙方收到通知時可以看到。">
        <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="例如：人手平衡，準。" />
      </Field>
      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
      <div className="flex flex-row items-center justify-end gap-2">
        <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
        {canDecide ? (
          <>
            <button onClick={() => void decide("rejected")} className="btn-danger text-sm" disabled={busy}>
              <ThumbsDown className="h-4 w-4" /> 拒絕
            </button>
            <button onClick={() => void decide("approved")} className="btn-primary text-sm" disabled={busy}>
              <ThumbsUp className="h-4 w-4" /> 通過
            </button>
          </>
        ) : (
          <span className="text-xs text-text-muted">您目前的角色不能審核換班。</span>
        )}
      </div>
    </div>
  );
}

void CheckCircle2;
